// Backend ki utils/posBilling.js ki exact copy (ESM). Sirf preview ke liye — final amount backend calculate karta hai.

/**
 * POS bill calculation — backend aur frontend dono me SAME logic (frontend copy: src/utils/posBilling.js).
 * Backend hi final truth hai; frontend sirf preview dikhata hai.
 *
 * Order of discounts:
 *  1. Item price bargaining  -> line ka unitPrice hi kam ho jaata hai (listPrice se compare karke "itemDiscount" report hota hai)
 *  2. Coupon                 -> subtotal par (PERCENTAGE max cap ke saath / FLAT)
 *  3. Manual bill discount   -> coupon ke baad bache amount par (₹ ya %)
 *  Coupon + manual dono har line par uske amount ke ratio me baant'te hain (GST sahi nikle)
 *
 * GST: unitPrice inclusive; taxable = net / (1 + gst%); intra-state CGST = SGST = tax/2
 */

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/* Coupon ka discount amount (Coupon model ke fields) */
export function couponDiscountFor(coupon, subtotal) {
  if (!coupon) return { amount: 0, eligible: false, shortBy: 0 };

  const min = Number(coupon.minimumCartValue) || 0;
  if (subtotal < min) return { amount: 0, eligible: false, shortBy: round2(min - subtotal) };

  const value = Number(coupon.discountValue) || 0;
  let amount =
    coupon.discountType === "PERCENTAGE" ? (subtotal * Math.min(value, 100)) / 100 : value;

  if (coupon.discountType === "PERCENTAGE" && Number(coupon.maximumDiscount) > 0) {
    amount = Math.min(amount, Number(coupon.maximumDiscount));
  }
  amount = round2(Math.min(amount, subtotal));
  return { amount, eligible: true, shortBy: 0 };
}

export function computeBill(lines = [], discountInput = {}, coupon = null) {
  const mode = discountInput.mode === "percent" ? "percent" : "flat";
  const value = Math.max(Number(discountInput.value) || 0, 0);

  const withGross = lines.map((l) => {
    const unitPrice = Number(l.unitPrice) || 0;
    const listPrice = Number(l.listPrice ?? l.unitPrice) || 0;
    const qty = Number(l.qty) || 0;
    return {
      ...l,
      qty,
      unitPrice,
      listPrice,
      priceEdited: round2(unitPrice) !== round2(listPrice),
      gstRate: Number(l.gstRate) || 0,
      grossAmount: round2(unitPrice * qty),
    };
  });

  const subtotal = round2(withGross.reduce((s, l) => s + l.grossAmount, 0));
  const itemDiscount = round2(
    withGross.reduce((s, l) => s + Math.max(l.listPrice - l.unitPrice, 0) * l.qty, 0),
  );

  const cp = couponDiscountFor(coupon, subtotal);
  const couponDiscount = cp.amount;

  const afterCoupon = round2(subtotal - couponDiscount);
  const manualDiscount =
    mode === "percent"
      ? round2((afterCoupon * Math.min(value, 100)) / 100)
      : round2(Math.min(value, afterCoupon));

  const discount = round2(couponDiscount + manualDiscount);

  let allocated = 0;
  let taxable = 0;
  let totalTax = 0;
  let totalQty = 0;

  const items = withGross.map((l, i) => {
    const isLast = i === withGross.length - 1;
    const share =
      subtotal > 0
        ? isLast
          ? round2(discount - allocated)
          : round2((discount * l.grossAmount) / subtotal)
        : 0;
    allocated = round2(allocated + share);

    const netAmount = round2(l.grossAmount - share);
    const taxableAmount = round2(netAmount / (1 + l.gstRate / 100));
    const taxAmount = round2(netAmount - taxableAmount);

    taxable += taxableAmount;
    totalTax += taxAmount;
    totalQty += l.qty;

    return { ...l, discountAmount: share, netAmount, taxableAmount, taxAmount };
  });

  taxable = round2(taxable);
  totalTax = round2(totalTax);
  const cgst = round2(totalTax / 2);
  const sgst = round2(totalTax - cgst);

  const netTotal = round2(subtotal - discount);
  const grandTotal = Math.round(netTotal);
  const roundOff = round2(grandTotal - netTotal);

  return {
    items,
    totalQty,
    discountInput: { mode, value },
    coupon: coupon ? { eligible: cp.eligible, shortBy: cp.shortBy, amount: couponDiscount } : null,
    totals: {
      subtotal,
      itemDiscount, // bargaining se kitna kam kiya (info only, subtotal me already included)
      couponDiscount,
      manualDiscount,
      discount, // coupon + manual
      taxable,
      cgst,
      sgst,
      totalTax,
      roundOff,
      grandTotal,
    },
  };
}

export const inr = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* 12345.5 -> "Rupees Twelve Thousand Three Hundred Forty Five and Fifty Paise Only" */
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigits = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}`.trim());
const threeDigits = (n) =>
  n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred ${twoDigits(n % 100)}`.trim() : twoDigits(n);

export function amountInWords(amount) {
  const rupees = Math.floor(Math.abs(Number(amount) || 0));
  const paise = Math.round((Math.abs(Number(amount) || 0) - rupees) * 100);
  if (!rupees && !paise) return "Rupees Zero Only";

  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (rest) parts.push(threeDigits(rest));

  let words = `Rupees ${parts.join(" ")}`;
  if (paise) words += ` and ${twoDigits(paise)} Paise`;
  return `${words} Only`;
}